import React from 'react';
import { Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { CopySpan, DataGrid, HoverBgButton, message } from 'components/common';
import { COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION, COMMON_MSG_DEFAULT_DISPLAY_DURATION } from 'constants/config';
import { deleteWarehouseBatchTransferLine, fetchWarehouseBatchTransferDetail } from 'services/inventory';
import { getProfileSettingValue } from 'utils';

type Props = {
  editable?: boolean;
  onLoadTransfer?: Function;
  transferNumber: number;
  version?: number;
};

const TransferLineGrid = (props: Props) => {
  const { useState } = React;
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lines, setLines] = useState<StringKAnyVPair[]>([]);
  const [lotEnable, setLotEnable] = useState(false);
  const [uomEnable, setUomEnable] = useState(false);
  const [version, setVersion] = useState(props.version);

  const columns = [
    {
      header: 'SKU',
      name: 'sku',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.sku);
      },
    },
    {
      header: 'UOM',
      name: 'uom',
      render(row: any) {
        const { data } = row;

        return `${data.uom}(${data.uomRate})`;
      },
    },
    {
      header: 'LOT#',
      name: 'lotNumber',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.lotNumber);
      },
    },
    {
      header: 'From Warehouse',
      name: 'fromWarehouseCode',
    },
    {
      header: 'From Location',
      name: 'fromLocationCode',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.fromLocationCode);
      },
    },
    {
      header: 'Quantity',
      headerAlign: 'start',
      name: 'quantity',
      textAlign: 'end',
    },
    {
      header: 'To Warehouse',
      name: 'toWarehouseCode',
    },
    {
      header: 'To Location',
      name: 'toLocationCode',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.toLocationCode);
      },
    },
    {
      header: 'Enter By',
      name: 'createBy',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.createBy);
      },
    },
    {
      header: 'Note',
      name: 'note',
      render(row: any) {
        const { data } = row;

        return renderCopyableCell(data.note);
      },
    },
    {
      align: 'left',
      header: 'Action',
      maxWidth: 80,
      //minWidth: 80,
      name: '',
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return (
          <HoverBgButton
            className="p-0-4"
            hovertype="danger"
            onClick={() => deleteLine(data)}
            size="small"
          >
            <DeleteOutlined />
          </HoverBgButton>
        );
      },
    },
  ];

  const deleteLine = (line: StringKAnyVPair) => {
    Modal.confirm({
      centered: true,
      content: 'Are you sure to delete this line?',
      title: 'Confirm',
      onOk: async () => {
        const idx = lines.findIndex(l => l.warehouseBatchTransferLineNum === line.warehouseBatchTransferLineNum);

        if (idx > -1) {
          setIsLoading(true);
          try {
            const res = await deleteWarehouseBatchTransferLine(
              line.warehouseBatchTransferNum,
              line.warehouseBatchTransferLineNum,
            );

            //console.log('del', res);
            if (res) {
              lines.splice(idx, 1);
              setLines([ ...lines ]);
              message.success({
                content: 'Deleted successfully',
                duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
              });
            }
          } catch(e) {
            message.error({
              content: `Deleted line error: ${e}`,
              duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
            });
          } finally {
            setIsLoading(false);
          }
        }
      },
    });
  };

  const getLineColumns = (cols: StringKAnyVPair[]) => {
    const list: StringKAnyVPair[] = [];

    for (let i = 0; i < cols.length; i++) {
      if (!lotEnable && cols[i].name === 'lotNumber') continue;
      if (!uomEnable && cols[i].name === 'uom') continue;
      if (!props.editable && i === cols.length - 1) continue;

      list.push(cols[i]);
    }

    return list;
  };

  // eslint-disable-next-line
  const getProfileSettings = async () => {
    const res1 = await getProfileSettingValue('EnableLOT', '0');
    const res2 = await getProfileSettingValue('EnableUOM', '0');

    setLotEnable(res1 === '1');
    setUomEnable(res2 === '1');
  };

  // eslint-disable-next-line
  const loadDetail = async (tn: number) => {
    setIsLoading(true);
    try {
      const detail = await fetchWarehouseBatchTransferDetail(tn);

      if (detail) {
        detail.lines.sort((a: any, b: any) => b.warehouseBatchTransferLineNum - a.warehouseBatchTransferLineNum);
        setLines(detail.lines);

        if (props.onLoadTransfer) props.onLoadTransfer(detail);
      }
    } catch(e) {
    } finally {
      setIsLoading(false);
    }
  };

  const renderCopyableCell = (text: string) => {
    return (
      <CopySpan
        inGrid
        value={text}
      />
    );
  };

  React.useEffect(() => {
    if (!inited) {
      getProfileSettings();
      loadDetail(props.transferNumber);
      setInited(true);
    }

    if (version !== props.version) {
      loadDetail(props.transferNumber);
      setVersion(props.version);
    }
  }, [
    getProfileSettings,
    inited,
    loadDetail,
    props,
    version,
  ]);

  return (<>
    {<DataGrid
      columns={getLineColumns(columns)}
      //count={gridCount}
      dataSource={lines}
      //limit={gridPageSize.current}
      loading={isLoading}
      //onLimitChange={onLimitChange}
      //onSkipChange={onSkipChange}
      //pageSizes={[5, 20, 50, 100, 200]}
      pagination={false}
      rowKey="warehouseBatchTransferLineNum"
      //style={{height: 'calc(100vh - 240px)', maxHeight: 600}}
      style={{ height: '100%' }}
    />}
  </>);
};

export default TransferLineGrid;

export const exportColumns = [
  {
    header: 'SKU',
    name: 'sku',
  },
  {
    header: 'UOM',
    name: 'uom',
  },
  {
    header: 'LOT#',
    name: 'lotNumber',
  },
  {
    header: 'From Warehouse',
    name: 'fromWarehouseCode',
  },
  {
    header: 'From Location',
    name: 'fromLocationCode',
  },
  {
    header: 'Quantity',
    headerAlign: 'start',
    name: 'quantity',
    textAlign: 'end',
  },
  {
    header: 'To Warehouse',
    name: 'toWarehouseCode',
  },
  {
    header: 'To Location',
    name: 'toLocationCode',
  },
  {
    header: 'Enter By',
    name: 'createBy',
  },
  {
    header: 'Note',
    name: 'note',
  },
];

export const mapCsvData = (data: StringKAnyVPair[]) => {
  return data.map(e => {
    return { ...e, uom: `${e.uom}(${e.uomRate})` };
  });
};
